import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { useAdminAuth } from "../context/AdminAuthContext";
import { ADMIN } from "../constants/testIds";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function AdminLoginPage() {
  const { adminLogin, isAdminAuthenticated } = useAdminAuth();
  const navigate = useNavigate();
  const [needsSetup, setNeedsSetup] = useState(null);
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isAdminAuthenticated) { navigate("/sp-command-center"); return; }
    axios.get(`${API}/admin/setup-status`).then(({ data }) => setNeedsSetup(data.needs_setup)).catch(() => setNeedsSetup(false));
  }, [isAdminAuthenticated]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (needsSetup) {
        const { data } = await axios.post(`${API}/admin/setup`, form, { withCredentials: true });
        adminLogin({ email: form.email, id: data.admin?.id });
      } else {
        const { data } = await axios.post(`${API}/admin/login`, form, { withCredentials: true });
        adminLogin(data.admin);
      }
      navigate("/sp-command-center");
    } catch (err) {
      setError(err.response?.data?.detail || "Authentication failed.");
    } finally {
      setLoading(false);
    }
  };

  if (needsSetup === null) return <div className="min-h-screen bg-zinc-950" />;

  return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center relative overflow-hidden">
      <div
        className="absolute inset-0 opacity-10 bg-cover bg-center"
        style={{ backgroundImage: "url('https://images.pexels.com/photos/7641148/pexels-photo-7641148.jpeg?auto=compress&cs=tinysrgb&w=1200')" }}
      />
      <div className="absolute inset-0 bg-zinc-950/80" />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
        className="relative z-10 w-full max-w-sm px-6"
      >
        {/* Logo */}
        <div className="text-center mb-12 flex flex-col items-center">
          <img
            src="https://customer-assets.emergentagent.com/job_investment-portal-38/artifacts/5nykqmge_Green%20and%20Gold%20Professional%20Letter%20A%20Logo.png"
            alt="Scarlett Properties"
            className="w-28 h-auto mb-3"
          />
          <div className="text-[9px] tracking-[0.5em] text-zinc-400 uppercase">CMS</div>
          <div className="w-6 h-px bg-[#C8102E] mx-auto mt-3" />
        </div>

        {/* Form */}
        <div className="bg-zinc-900/80 border border-zinc-800 p-8 backdrop-blur-sm">
          <p className="text-[9px] tracking-[0.4em] text-white uppercase mb-6 font-light">
            {needsSetup ? "Initial Setup — Create Admin Account" : "Administrator Access"}
          </p>

          <form onSubmit={handleSubmit} className="space-y-5">
            <input
              type="email"
              value={form.email}
              onChange={e => setForm(f => ({...f, email: e.target.value}))}
              placeholder="Email Address"
              data-testid={needsSetup ? ADMIN.setupEmail : ADMIN.loginEmail}
              required
              className="w-full bg-transparent border-b border-zinc-700 text-white py-2.5 text-sm placeholder:text-zinc-600 focus:outline-none focus:border-zinc-400 transition-colors font-light tracking-wide"
            />
            <input
              type="password"
              value={form.password}
              onChange={e => setForm(f => ({...f, password: e.target.value}))}
              placeholder="Password"
              data-testid={needsSetup ? ADMIN.setupPassword : ADMIN.loginPassword}
              required
              className="w-full bg-transparent border-b border-zinc-700 text-white py-2.5 text-sm placeholder:text-zinc-600 focus:outline-none focus:border-zinc-400 transition-colors font-light"
            />
            {error && <p className="text-[#C8102E] text-xs font-light">{error}</p>}
            <button
              type="submit"
              disabled={loading}
              data-testid={needsSetup ? ADMIN.setupSubmit : ADMIN.loginSubmit}
              className="w-full bg-[#C8102E] hover:bg-[#A50D25] text-white text-[10px] tracking-[0.4em] uppercase py-4 transition-colors duration-300 disabled:opacity-50 mt-2 font-light"
            >
              {loading ? "Please wait..." : needsSetup ? "Create Account" : "Sign In"}
            </button>
          </form>
        </div>

        {needsSetup && (
          <p className="text-center text-white/50 text-[10px] tracking-wider mt-5 font-light">
            First-time setup. This form will be disabled after admin creation.
          </p>
        )}
      </motion.div>
    </div>
  );
}
